import { useParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { brandTokens } from '@wineryxplorer/brand-tokens';
import { buildCheckInQrUrl } from '@wineryxplorer/shared';
import { PageContainer } from '../components/PageContainer';
import { PermissionGuard } from '../components/PermissionGuard';
import { Button } from '../components/ui/Button';
import { ServerError } from '../components/ui/ErrorDisplay';
import { PageLoading } from '../components/ui/LoadingSpinner';
import { Typography } from '../components/ui/Typography';
import { useVenuePermissions } from '../hooks/useVenuePermissions';
import { trpc } from '../lib/trpc';

// Print only the sheet: everything else is hidden (visibility, so the sheet's
// own ancestors still lay it out) and the page margins are dropped.
const printStyles = `
@media print {
  @page { margin: 0; }
  body * { visibility: hidden; }
  #check-in-qr-sheet, #check-in-qr-sheet * { visibility: visible; }
  #check-in-qr-sheet { position: absolute; left: 0; top: 0; width: 100%; }
}
`;

interface QrSheetProps {
  venueId: string;
  venueName: string;
}

const QrSheet = ({ venueId, venueName }: QrSheetProps) => {
  const settings = trpc.checkIn.venueQrSettings.useQuery({ venueId });

  if (settings.isLoading) {
    return <PageLoading message="Loading QR code..." />;
  }

  if (settings.error || !settings.data) {
    return (
      <ServerError
        title="Unable to load the check-in QR code"
        message={settings.error?.message ?? 'Please try again.'}
        onRetry={() => void settings.refetch()}
      />
    );
  }

  const { requiresQr, token } = settings.data;
  if (!requiresQr || !token) {
    return (
      <Typography variant="bodyMedium" className="text-gray-700 text-center py-12">
        Turn on Require a QR scan to check in on the venue management page first.
      </Typography>
    );
  }

  return (
    <>
      <style>{printStyles}</style>
      <div className="flex justify-end mb-4">
        <Button className="min-h-[44px]" onClick={() => window.print()}>
          Print
        </Button>
      </div>
      <div
        id="check-in-qr-sheet"
        className="flex flex-col items-center text-center gap-4 py-12 px-4 bg-gray-50"
      >
        <img src="/winex-logo.png" alt="WineryXplorer" className="h-12 w-auto" />
        <Typography variant="heading1" className="text-gray-900">
          {venueName}
        </Typography>
        <Typography variant="bodyLarge" className="text-gray-700">
          Check in on WineryXplorer
        </Typography>
        <QRCodeSVG
          value={buildCheckInQrUrl(window.location.origin, venueId, token)}
          size={240}
          level="M"
          marginSize={2}
          fgColor={brandTokens.semantic.textPrimary}
          bgColor={brandTokens.semantic.background}
        />
        <Typography variant="bodyMedium" className="text-gray-700 max-w-sm">
          Open the WineryXplorer app, tap +, then Check in at a Winery, and scan this code.
        </Typography>
      </div>
    </>
  );
};

export const VenueCheckInQr = () => {
  const { id } = useParams<{ id: string }>();
  const venueId = id || '';
  const { venue, isLoading, error } = useVenuePermissions({ venueId });

  if (isLoading) {
    return (
      <div className="px-0 md:px-4 pt-2 pb-4">
        <PageContainer>
          <PageLoading message="Loading venue..." />
        </PageContainer>
      </div>
    );
  }

  if (error || !venue) {
    return (
      <div className="px-0 md:px-4 pt-2 pb-4">
        <PageContainer>
          <ServerError
            title="Error Loading Venue"
            message={error?.message ?? "The venue you're looking for doesn't exist."}
            onRetry={() => window.location.reload()}
          />
        </PageContainer>
      </div>
    );
  }

  return (
    <PermissionGuard venueId={venueId}>
      <div className="px-0 md:px-4 pt-2 pb-4">
        <PageContainer>
          <QrSheet venueId={venueId} venueName={venue.name} />
        </PageContainer>
      </div>
    </PermissionGuard>
  );
};
